import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Control, Controller, FieldValues, Path } from "react-hook-form";

import type { ComboboxData, GroupType, Option, OptionValue } from "./types";
import { flattenItems, isGroupArray } from "./utils";

export type ComboboxProps<TFieldValues extends FieldValues = FieldValues> = {
  items: Option[] | GroupType[];
  value?: OptionValue[];
  defaultValue?: OptionValue[];
  onChange?: (value: OptionValue[]) => void;
  multiple?: boolean;
  onAddItem?: (value: string) => void;
  children: React.ReactNode | ((combobox: ComboboxData) => React.ReactNode);
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  externalFilter?: boolean; // When true, caller is responsible for filtering. Useful with infinite scroll since set this to false when fetching and true when not fetching
  // React Hook Form support
  control?: Control<TFieldValues>;
  name?: Path<TFieldValues>;
};

function ComboboxInner({
  items,
  value: controlledValue,
  defaultValue = [],
  externalFilter = false,
  onChange,
  multiple = false,
  onAddItem,
  children,
  open: controlledOpen,
  onOpenChange,
}: Omit<ComboboxProps, "control" | "name">) {
  const [internalValue, setInternalValue] = useState<OptionValue[]>(defaultValue);
  const [filterValue, setFilterValue] = useState("");
  const [internalOpen, setInternalOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef<HTMLDivElement>(null);
  const inputElementRef = useRef<HTMLInputElement>(null);

  const value = controlledValue !== undefined ? controlledValue : internalValue;
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;

  const setOpen = useCallback(
    (newOpen: boolean) => {
      if (controlledOpen === undefined) {
        setInternalOpen(newOpen);
      }
      onOpenChange?.(newOpen);
    },
    [controlledOpen, onOpenChange],
  );

  const handleChange = useCallback(
    (newValue: OptionValue[]) => {
      if (controlledValue === undefined) {
        setInternalValue(newValue);
      }
      onChange?.(newValue);
    },
    [controlledValue, onChange],
  );

  const filteredItems = useMemo(() => {
    if (!filterValue.trim()) return items;

    if (externalFilter) return items;

    const lowerFilter = filterValue.toLowerCase();

    if (isGroupArray(items)) {
      return items
        .map((group) => ({
          ...group,
          options: group.options.filter((option) =>
            option.label.toLowerCase().includes(lowerFilter),
          ),
        }))
        .filter((group) => group.options.length > 0);
    }

    return items.filter((option) => option.label.toLowerCase().includes(lowerFilter));
  }, [items, filterValue, externalFilter]);

  const selectItem = useCallback(
    (item: Option) => {
      const isSelected = value.includes(item.value);

      if (multiple) {
        if (isSelected) {
          handleChange(value.filter((v) => v !== item.value));
        } else {
          handleChange([...value, item.value]);
        }
      } else {
        handleChange([item.value]);
        setOpen(false);
        setFilterValue("");
      }
    },
    [value, multiple, handleChange, setOpen],
  );

  const selectAll = useCallback(() => {
    if (!multiple) return;
    const allFilteredOptions = flattenItems(filteredItems);
    handleChange(allFilteredOptions.map((opt) => opt.value));
  }, [multiple, filteredItems, handleChange]);

  const deselectAll = useCallback(() => {
    if (!multiple) return;
    handleChange([]);
  }, [multiple, handleChange]);

  useEffect(() => {
    if (!open || !inputElementRef.current) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      const flatOptions = flattenItems(filteredItems);
      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setHighlightedIndex((prev) => Math.min(prev + 1, flatOptions.length - 1));
          break;
        case "ArrowUp":
          e.preventDefault();
          setHighlightedIndex((prev) => Math.max(prev - 1, -1));
          break;
        case "Enter":
          e.preventDefault();
          if (highlightedIndex >= 0 && highlightedIndex < flatOptions.length) {
            selectItem(flatOptions[highlightedIndex]);
          }
          break;
        case "Escape":
          e.preventDefault();
          setOpen(false);
          break;
      }
    };

    const element = inputElementRef.current;
    element.addEventListener("keydown", handleKeyDown);
    return () => element.removeEventListener("keydown", handleKeyDown);
  }, [open, filteredItems, highlightedIndex, selectItem, setOpen]);

  useEffect(() => {
    if (open) {
      const timeoutId = setTimeout(() => {
        inputElementRef.current?.focus();
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [open]);

  const comboboxData: ComboboxData = {
    items,
    value,
    onChange: handleChange,
    filterValue,
    setFilterValue,
    open,
    setOpen,
    multiple,
    onAddItem,
    filteredItems,
    highlightedIndex,
    setHighlightedIndex,
    selectItem,
    selectAll,
    deselectAll,
    /* InputRef is for positioning and the input element ref is for focus management on the search */
    inputRef,
    inputElementRef,
  };

  return <>{typeof children === "function" ? children(comboboxData) : children}</>;
}

export function Combobox<TFieldValues extends FieldValues = FieldValues>({
  control,
  name,
  ...props
}: ComboboxProps<TFieldValues>) {
  if (control && name) {
    return (
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <ComboboxInner
            {...props}
            value={field.value || []}
            onChange={(value) => field.onChange(value)}
          />
        )}
      />
    );
  }

  return <ComboboxInner {...props} />;
}
